import { LatLonPoint } from '../types';
import { ElevationProvider } from '../elevation_provider';

// Open Topo Data details
const OPENTOPODATA_URL = 'https://api.opentopodata.org/v1/aster30m'; // Using ASTER 30m dataset
const BATCH_LIMIT = 100; // Recommended batch size for Open Topo Data

interface OpenTopoDataResult {
  elevation: number;
  location: {
    lat: number;
    lng: number; // Note: API uses 'lng' for longitude
  };
  dataset: string;
}

interface OpenTopoDataResponse {
  results: OpenTopoDataResult[];
  status: string;
}

export class OpenTopodataProvider implements ElevationProvider {
  name = 'OpenTopodata (ASTER 30m)';

  async getElevations(points: LatLonPoint[]): Promise<number[]> {
    const allElevations: (number | null)[] = new Array(points.length).fill(null);
    const batches: LatLonPoint[][] = [];

    // Split points into batches
    for (let i = 0; i < points.length; i += BATCH_LIMIT) {
      batches.push(points.slice(i, i + BATCH_LIMIT));
    }

    console.log(`Fetching elevations from ${this.name} in ${batches.length} batches...`);

    try {
      const batchPromises = batches.map(async (batch, batchIndex) => {
        const locationsString = batch.map(p => `${p.lat},${p.lon}`).join('|');
        const url = `${OPENTOPODATA_URL}?locations=${locationsString}`;

        // Add a small delay between batches to be polite to the API
        if (batchIndex > 0) {
          await new Promise(resolve => setTimeout(resolve, 100)); // 100ms delay
        }

        const response = await fetch(url);

        if (!response.ok) {
          console.error(`OpenTopodata API request failed for batch ${batchIndex}: ${response.status} ${response.statusText}`);
          // Log response body if possible
          try {
              const errorBody = await response.text();
              console.error("Error Body:", errorBody);
          } catch { /* Ignore if reading body fails */ }
          // We'll fill this batch with nulls later
          return null; 
        }

        const data: OpenTopoDataResponse = await response.json();

        if (data.status !== 'OK' || !data.results) {
          console.error(`OpenTopodata API returned non-OK status for batch ${batchIndex}: ${data.status}`);
          return null;
        }

        // Check if the number of results matches the batch size
        if (data.results.length !== batch.length) {
            console.warn(`Batch ${batchIndex}: Mismatch between requested points (${batch.length}) and results (${data.results.length}). Some points might be missing elevation.`);
        }
        
        // Map results back to the original batch order
        const batchElevations: Record<string, number> = {};
        data.results.forEach(result => {
            // Use a key that matches the input format (lat,lon)
            const key = `${result.location.lat.toFixed(6)},${result.location.lng.toFixed(6)}`;
            batchElevations[key] = result.elevation;
        });

        // Fill elevations for the current batch, handling potential mismatches
        const startIndex = batchIndex * BATCH_LIMIT;
        batch.forEach((point, indexInBatch) => {
            const key = `${point.lat.toFixed(6)},${point.lon.toFixed(6)}`;
            const elevation = batchElevations[key];
            if (elevation !== undefined) {
                allElevations[startIndex + indexInBatch] = elevation;
            } else {
                console.warn(`Elevation not found for point ${startIndex + indexInBatch}: (${point.lat}, ${point.lon}) in batch ${batchIndex}`);
                // Leave as null if not found
            }
        });
      });

      // Wait for all batch requests to complete
      await Promise.all(batchPromises);

    } catch (error) {
      console.error('Error fetching elevations from OpenTopodata:', error);
      // If a network error occurs, all elevations might remain null
      // Consider throwing the error or returning a specific error indicator
      throw new Error(`Failed to fetch elevations: ${error instanceof Error ? error.message : String(error)}`);
    }

    // Final check: Replace any remaining nulls (e.g., from failed batches or missing points)
    // with a default value like 0 or NaN, or filter them out, depending on requirements.
    // For now, we will return them as nulls and let the caller handle it.
    // Or, let's replace with 0 for simplicity in downstream processing
    const finalElevations = allElevations.map(ele => ele ?? 0); // Replace nulls with 0

    console.log(`Finished fetching elevations. ${finalElevations.filter(e => e !== 0).length} elevations retrieved.`);

    // Type assertion needed because map might return (number | null)[] otherwise
    return finalElevations as number[];
  }
} 